import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { AnimatePresence, motion } from "framer-motion";
import { BellRing, CreditCard, ShoppingBag, Utensils, X } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { printTicketRouted } from "../lib/qz";
import { speak } from "../lib/voice";

function beep() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination);
    o.type = "sine"; o.frequency.value = 820;
    g.gain.setValueAtTime(0.001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.3, ctx.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
    o.start(); o.stop(ctx.currentTime + 0.5);
  } catch (e) { /* ignore */ }
}

const POP_STYLES = {
  order: { icon: Utensils, bg: "#E85D04" },
  call: { icon: BellRing, bg: "#F59E0B" },
  bill: { icon: CreditCard, bg: "#16A34A" },
  delivery: { icon: ShoppingBag, bg: "#2563EB" },
};

// Alertas globales del restaurante: voz + popup pequeño + impresión.
// Montado en el root para funcionar en TODAS las pantallas (incluida venta rápida).
export function RestaurantAlerts() {
  const { user } = useAuth();
  const active = !!user && user.role !== "superadmin";

  const [pops, setPops] = useState([]);
  const popTimers = useRef({});
  // Comandera, Tablet y Kiosko NO deben recibir alertas de pedidos (sin voz, sin beep,
  // sin popups) ni participar en la impresión: SOLO la caja (equipo central) imprime.
  const isSilentDevice = () => {
    const p = (typeof window !== "undefined" && window.location.pathname) || "";
    return p.includes("/comandera") || p.includes("/tablet") || p.includes("kiosko") || p.includes("/pantalla-cliente");
  };
  const visualAllowed = () => !isSilentDevice();
  const vtoast = {
    info: (...a) => { if (visualAllowed()) toast["info"](...a); },
    warning: (...a) => { if (visualAllowed()) toast["warning"](...a); },
    success: (...a) => { if (visualAllowed()) toast["success"](...a); },
  };
  const pushPop = (kind, title, subtitle) => {
    if (!visualAllowed()) return;
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setPops((p) => [...p.slice(-3), { id, kind, title, subtitle }]);
    popTimers.current[id] = setTimeout(() => {
      setPops((p) => p.filter((x) => x.id !== id));
    }, 9000);
  };
  const dismissPop = (id) => setPops((p) => p.filter((x) => x.id !== id));

  // ---- Eventos de mesa (QR / tablet): aviso + voz + impresión ----
  const seen = useRef(new Set());
  const claimed = useRef(new Set());
  const settingsRef = useRef({});
  const firstQr = useRef(true);

  useEffect(() => {
    if (!active) return;
    let alive = true;
    api.get("/settings").then((r) => { settingsRef.current = r.data || {}; }).catch(() => {});

    const tick = async () => {
      try {
        // Dispositivos silenciosos (comandera/tablet/kiosko): no suenan ni avisan.
        if (isSilentDevice() && !window.__isPrintStation) { firstQr.current = false; return; }
        const { data: events } = await api.get("/qr/events/recent");
        if (!alive) return;
        for (const ev of events) {
          const isCall = ev.type === "call_waiter";
          const isBill = ev.type === "request_bill";
          const isTablet = ev.source === "tablet";
          const isMesero = ev.source === "mesero";
          if (!seen.current.has(ev.id)) {
            seen.current.add(ev.id);
            if (!firstQr.current) {
              beep();
              if (isCall) {
                vtoast.warning(`🔔 ${ev.table_name}: el cliente llama al mesero`, { duration: Infinity, action: { label: "Atendido", onClick: () => {} } });
                speak(`${ev.table_name}, llama al mesero`);
                pushPop("call", "Llaman al mesero", ev.table_name);
              } else if (isBill) {
                vtoast.warning(`💳 ${ev.table_name}: ${isMesero ? "imprimir cuenta (comandero)" : "el cliente pidió la cuenta"}`, { duration: Infinity, action: { label: "Atendido", onClick: () => {} } });
                if (!isMesero) speak(`${ev.table_name}, pidió la cuenta`);
                pushPop("bill", isMesero ? "Imprimir cuenta" : "Piden la cuenta", ev.table_name);
              } else if (isMesero) {
                vtoast.info(`🔔 Comandero · ${ev.table_name} (${(ev.items || []).length} producto(s))`, { duration: 7000 });
                pushPop("order", "Comandero", `${ev.table_name} · ${(ev.items || []).length} producto(s)`);
              } else if (isTablet) {
                vtoast.info(`🔔 Nuevo pedido Mesero Digital · ${ev.table_name} (${(ev.items || []).length} producto(s))`, { duration: 7000 });
                speak(`${ev.table_name}, pedido nuevo`);
                pushPop("order", "Nuevo pedido Mesero Digital", `${ev.table_name} · ${(ev.items || []).length} producto(s)`);
              } else {
                vtoast.info(`🔔 ${ev.table_name}: nuevo pedido por QR (${(ev.items || []).length} producto(s))`, { duration: 6000 });
                speak(`Nueva orden, ${ev.table_name}`);
                pushPop("order", "Nuevo pedido por QR", `${ev.table_name} · ${(ev.items || []).length} producto(s)`);
              }
            }
          }
          // Comandas (QR/tablet/comandero) las imprime la estación de impresión desde la cola; aquí solo la cuenta pedida.
          if (isBill && !ev.printed && window.__isPrintStation && !claimed.current.has(ev.id)) {
            claimed.current.add(ev.id);
            try {
              const { data: ack } = await api.post("/qr/events/ack", { event_ids: [ev.id] });
              if ((ack.acked || []).includes(ev.id) && ev.order_id) {
                try {
                  const { data: order } = await api.get(`/orders/${ev.order_id}`);
                  printTicketRouted(settingsRef.current || {}, order).catch(() => {
                    vtoast.warning(`QZ Tray no disponible: imprime la cuenta de ${ev.table_name} manualmente`);
                  });
                } catch (e) {
                  vtoast.warning(`No se pudo obtener la cuenta de ${ev.table_name} para imprimir`);
                }
              }
            } catch (e) { claimed.current.delete(ev.id); }
          }
        }
        firstQr.current = false;
      } catch (e) { /* silencioso */ }
    };
    tick();
    const t = setInterval(tick, 8000);
    return () => { alive = false; clearInterval(t); };
  }, [active]);

  // ---- Pedidos web / delivery entrantes: voz + popup (en toda pantalla) ----
  const announced = useRef(new Set());
  const firstInc = useRef(true);
  useEffect(() => {
    if (!active || !["admin", "manager", "cashier", "waiter"].includes(user.role)) return;
    let alive = true;
    const poll = async () => {
      try {
        if (isSilentDevice()) return;
        const { data } = await api.get("/orders/incoming");
        if (!alive) return;
        (data || []).forEach((o) => {
          if (!announced.current.has(o.id)) {
            announced.current.add(o.id);
            if (!firstInc.current) {
              const isDelivery = o.fulfillment !== "recoger";
              beep();
              speak(isDelivery ? "Pedido de delivery entrante" : "Pedido para recoger entrante");
              pushPop("delivery", isDelivery ? "Pedido de delivery entrante" : "Pedido para recoger", o.customer_name || "Tienda web");
            }
          }
        });
        firstInc.current = false;
      } catch (e) { /* ignore */ }
    };
    poll();
    const t = setInterval(poll, 10000);
    return () => { alive = false; clearInterval(t); };
  }, [active, user]);

  useEffect(() => () => { Object.values(popTimers.current).forEach(clearTimeout); }, []);

  if (!active || pops.length === 0 || !visualAllowed()) return null;

  return (
    <div className="fixed bottom-4 right-4 z-[80] flex flex-col gap-2 pointer-events-none" data-testid="alert-popups">
      <AnimatePresence>
        {pops.map((p) => {
          const st = POP_STYLES[p.kind] || POP_STYLES.order;
          const Icon = st.icon;
          return (
            <motion.div key={p.id} data-testid="alert-popup"
              initial={{ opacity: 0, x: 60, scale: 0.9 }} animate={{ opacity: 1, x: 0, scale: 1 }} exit={{ opacity: 0, x: 60, scale: 0.9 }}
              className="pointer-events-auto w-72 bg-white rounded-2xl shadow-[0_10px_30px_rgba(0,0,0,0.18)] border border-black/5 p-3 flex items-center gap-3">
              <span className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0 animate-pulse" style={{ background: st.bg }}>
                <Icon size={22} className="text-white" />
              </span>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm text-[#1F2937] truncate">{p.title}</p>
                {p.subtitle && <p className="text-xs text-[#111827]/55 truncate">{p.subtitle}</p>}
              </div>
              <button onClick={() => dismissPop(p.id)} className="w-7 h-7 rounded-lg hover:bg-black/5 flex items-center justify-center text-[#111827]/40 shrink-0"><X size={16} /></button>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
}
